<template>
  <span class="base-svg-icon-component" :class="classes" :style="customSize">
    <component :is="icon" class="base-svg-icon-inner" />
  </span>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, shallowRef, watch } from 'vue'

type Color = 'primary' | 'secondary' | 'accent' | 'warning' | 'danger' | 'white' | 'info'
type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
interface Props {
  name: string
  color?: Color
  size?: Size
  width?: string | number
  height?: string | number
  noPointer?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  color: 'primary',
  size: 'sm',
  noPointer: false,
})

const classes = computed(() => {
  return {
    'base-svg-icon-component--primary': props.color === 'primary',
    'base-svg-icon-component--secondary': props.color === 'secondary',
    'base-svg-icon-component--accent': props.color === 'accent',
    'base-svg-icon-component--warning': props.color === 'warning',
    'base-svg-icon-component--danger': props.color === 'danger',
    'base-svg-icon-component--white': props.color === 'white',
    'base-svg-icon-component--info': props.color === 'info',
    'base-svg-icon-component--xs': props.size === 'xs' && !props.width && !props.height,
    'base-svg-icon-component--sm': props.size === 'sm' && !props.width && !props.height,
    'base-svg-icon-component--md': props.size === 'md' && !props.width && !props.height,
    'base-svg-icon-component--lg': props.size === 'lg' && !props.width && !props.height,
    'base-svg-icon-component--xl': props.size === 'xl' && !props.width && !props.height,
    'base-svg-icon-component--no-pointer': props.noPointer,
  }
})

const customSize = computed(() => {
  const styles: Record<string, string | number> = {}

  if (props.width) {
    styles.width = isNaN(Number(props.width)) ? props.width : `${props.width}px`
  }

  if (props.height) {
    styles.height = isNaN(Number(props.height)) ? props.height : `${props.height}px`
  }

  return styles
})

const icon = shallowRef()

watch(
  () => props.name,
  (newName) => {
    icon.value = defineAsyncComponent(() => import(`@/assets/icons/${newName}.svg`))
  },
  { immediate: true },
)
</script>

<style scoped>
.base-svg-icon-component {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
  flex-shrink: 0;
}

.base-svg-icon-inner {
  width: 100%;
  height: 100%;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: rotate 0.2s ease-in-out;
}

.base-svg-icon-component svg,
.base-svg-icon-inner svg {
  color: inherit;
  width: 100%;
  height: 100%;
}

.base-svg-icon-component--primary {
  color: var(--gray-darker);
}

.base-svg-icon-component--secondary {
  color: var(--gray-dark);
}

.base-svg-icon-component--accent {
  color: var(--cyan-primary);
}

.base-svg-icon-component--warning {
  color: var(--orange-light);
}

.base-svg-icon-component--danger {
  color: var(--red-danger);
}

.base-svg-icon-component--white {
  color: var(--white);
}

.base-svg-icon-component--info {
  color: var(--blue-primary);
}

.base-svg-icon-component--xs {
  width: 14px;
  height: 14px;
}

.base-svg-icon-component--sm {
  width: 20px;
  height: 20px;
}

.base-svg-icon-component--md {
  width: 28px;
  height: 28px;
}

.base-svg-icon-component--lg {
  width: 48px;
  height: 48px;
}

.base-svg-icon-component--xl {
  width: 62px;
  height: 62px;
}

.base-svg-icon-component--no-pointer {
  cursor: default;
}
</style>
